import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Banknote,
  BarChart3,
  CalendarDays,
  Check,
  CheckCircle2,
  Moon,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  UsersRound,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, ScrollReveal, SpotlightCard, cn } from '@agenda-facil/ui';

const features = [
  {
    icon: CalendarDays,
    title: 'Agenda que se organiza',
    text: 'Disponibilidade real, bloqueios e confirmações sem conflito de horário.',
  },
  {
    icon: Banknote,
    title: 'Caixa sem complicação',
    text: 'Abertura, movimentações, pagamentos e fechamento em poucos toques.',
  },
  {
    icon: UsersRound,
    title: 'Cliente bem cuidado',
    text: 'Histórico e preferências à mão para um atendimento mais próximo.',
  },
  {
    icon: BarChart3,
    title: 'Números que fazem sentido',
    text: 'Faturamento, ocupação e ticket médio traduzidos para a rotina.',
  },
] as const;

export function HomePage() {
  return (
    <>
      <MobileStickyCTA />
      <section id="hero" className="marketing-grid relative overflow-hidden border-b border-[var(--border)] py-16 sm:py-24 lg:py-28">
        <div className="ambient-glow-hero" aria-hidden="true" />
        <div className="page-shell relative grid items-center gap-14 lg:grid-cols-[.92fr_1.08fr]">
          <ScrollReveal variant="left">
            <span className="eyebrow">Feito para a rotina da barbearia</span>
            <h1 className="display text-balance mt-6 max-w-[720px] text-[clamp(3rem,7vw,5.7rem)] leading-[.94]">
              Sua barbearia no <span className="text-[var(--accent)]">ritmo certo.</span>
            </h1>
            <p className="mt-7 max-w-xl text-balance text-lg leading-8 text-[var(--muted-foreground)]">
              Agenda, equipe, clientes e caixa em um sistema simples de usar — do primeiro horário
              ao fechamento do dia.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" asChild>
                <Link to="/cadastro">
                  Começar 14 dias grátis <ArrowRight size={18} />
                </Link>
              </Button>
              <Button size="lg" variant="secondary" asChild>
                <Link to="/recursos">Conhecer os recursos</Link>
              </Button>
            </div>
            <SocialProof />
          </ScrollReveal>
          <ScrollReveal variant="right" delay={150}>
            <AgendaHeroDemo />
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 sm:py-28" id="recursos">
        <div className="page-shell">
          <ScrollReveal className="grid items-end gap-6 md:grid-cols-2">
            <div>
              <span className="eyebrow">Tudo conversa</span>
              <h2 className="display text-balance mt-5 text-4xl leading-tight sm:text-6xl">
                Menos remendo. Mais barbearia.
              </h2>
            </div>
            <p className="max-w-lg text-base leading-8 text-[var(--muted-foreground)] md:justify-self-end">
              Cada ferramenta foi desenhada para diminuir passos e devolver tempo a quem está
              atendendo.
            </p>
          </ScrollReveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {features.map(({ icon: Icon, title, text }, index) => (
              <ScrollReveal
                key={title}
                variant="scale"
                delay={(index + 1) * 100}
              >
                <SpotlightCard
                  className={`group h-full p-6 transition duration-300 hover:-translate-y-1 hover:border-[var(--border-strong)] ${index === 0 ? 'lg:rotate-[-1deg]' : index === 3 ? 'lg:rotate-[1deg]' : ''}`}
                >
                  <span className="grid size-12 place-items-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)] transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                    <Icon size={23} className="transition-transform duration-300 group-hover:scale-110" />
                  </span>
                  <h3 className="mt-8 text-lg font-extrabold">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[var(--muted-foreground)]">{text}</p>
                </SpotlightCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--brand-strong)] py-20 text-[#f8f4eb] sm:py-28">
        <div className="page-shell grid gap-14 lg:grid-cols-[.8fr_1.2fr]">
          <ScrollReveal variant="left">
            <span className="eyebrow">Comece ainda hoje</span>
            <h2 className="display text-balance mt-5 text-4xl leading-tight sm:text-6xl">
              Da primeira configuração ao primeiro horário.
            </h2>
            <p className="mt-6 max-w-md leading-7 text-white/60">
              Um caminho guiado, sem implantação longa e sem treinamento complicado.
            </p>
          </ScrollReveal>
          <ol className="grid gap-px overflow-hidden rounded-[28px] border border-white/15 bg-white/15">
            {[
              ['01', 'Cadastre a barbearia', 'Informe horários, equipe e serviços do seu jeito.'],
              [
                '02',
                'Compartilhe seu link',
                'O cliente escolhe e reserva sem precisar mandar mensagem.',
              ],
              ['03', 'Atenda e acompanhe', 'Agenda e caixa ficam atualizados para toda a equipe.'],
            ].map(([number, title, text], index) => (
              <ScrollReveal
                as="li"
                key={number}
                variant="fade-up"
                delay={(index + 1) * 120}
                className="grid gap-4 bg-[var(--brand-strong)] p-6 sm:grid-cols-[64px_1fr] sm:p-8"
              >
                <span className="display text-3xl text-[var(--accent)]">{number}</span>
                <div>
                  <h3 className="text-lg font-extrabold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/60">{text}</p>
                </div>
              </ScrollReveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="page-shell grid gap-6 lg:grid-cols-[1fr_1.25fr]">
          <ScrollReveal variant="left">
            <SpotlightCard className="relative h-full overflow-hidden bg-[var(--accent-soft)] p-8 sm:p-12">
              <Sparkles className="text-[var(--accent)] transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12" />
              <h2 className="display mt-10 text-4xl leading-tight sm:text-5xl">
                Feito para caber na mão e na rotina.
              </h2>
              <p className="mt-5 max-w-md leading-7 text-[var(--muted-foreground)]">
                Instale como aplicativo, use no balcão ou no celular e mantenha as ações importantes
                sempre ao alcance.
              </p>
            </SpotlightCard>
          </ScrollReveal>
          <ScrollReveal variant="right" delay={150}>
            <SpotlightCard className="h-full p-8 sm:p-12">
              <span className="eyebrow">Plano Essencial</span>
              <div className="mt-7 flex items-end gap-2">
                <span className="fraunces-num text-6xl">R$ 49</span>
                <span className="pb-2 text-sm text-[var(--muted-foreground)]">/ mês</span>
              </div>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  'Agenda online',
                  'Caixa e relatórios',
                  'Até 3 profissionais',
                  'Página de agendamento',
                  'PWA instalável',
                  'Suporte por e-mail',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm font-bold group/item">
                    <span className="grid size-6 place-items-center rounded-full bg-[var(--success-soft)] text-[var(--success)] transition-transform duration-200 group-hover/item:scale-110">
                      <Check size={14} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button className="mt-9 w-full sm:w-auto" size="lg" asChild>
                <Link to="/cadastro">
                  Testar sem cartão <ArrowRight size={18} />
                </Link>
              </Button>
            </SpotlightCard>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-y border-[var(--border)] bg-[var(--surface)] py-20 sm:py-24">
        <div className="page-shell grid gap-10 lg:grid-cols-[.7fr_1.3fr]">
          <ScrollReveal variant="left">
            <span className="eyebrow">Dúvidas comuns</span>
            <h2 className="display mt-5 text-4xl sm:text-5xl">Antes de abrir a agenda.</h2>
          </ScrollReveal>
          <div className="grid">
            {[
              [
                'Preciso instalar alguma coisa?',
                'Não. O sistema funciona no navegador e também pode ser instalado como PWA no celular ou computador.',
              ],
              [
                'Posso cancelar o teste?',
                'Sim. O teste não pede cartão e termina sem cobrança automática.',
              ],
              [
                'Meus clientes precisam criar conta?',
                'Não. Para agendar, basta informar os dados de contato ao final do fluxo.',
              ],
              [
                'Consigo usar com mais de um barbeiro?',
                'Sim. Os planos definem o limite de profissionais e cada um tem sua própria disponibilidade.',
              ],
            ].map(([question, answer], index) => (
              <ScrollReveal
                key={question}
                variant="fade-up"
                delay={(index + 1) * 80}
              >
                {index > 0 && <hr className="divider-fade" />}
                <details className="group py-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-extrabold">
                    <span>{question}</span>
                    <span className="text-[var(--accent)] transition-transform duration-200 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 max-w-2xl pr-8 text-sm leading-7 text-[var(--muted-foreground)]">
                    {answer}
                  </p>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="page-shell relative">
          <ScrollReveal variant="scale" className="relative">
            <div className="ambient-glow-cta" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[36px] bg-[var(--accent)] border border-white/20 dark:border-white/10 backdrop-blur-xl px-6 py-14 text-center text-[#231710] shadow-[var(--shadow-lg)] sm:px-12 sm:py-20">
              <div className="absolute inset-x-0 top-0 h-2 barber-stripe opacity-40" />
              <ShieldCheck className="mx-auto" size={30} />
              <h2 className="display text-balance mx-auto mt-6 max-w-3xl text-4xl leading-tight sm:text-6xl">
                Sua próxima semana pode ser bem mais simples.
              </h2>
              <p className="mx-auto mt-5 max-w-xl leading-7 text-[#3d2b22]/75">
                Configure em poucos minutos e experimente todos os recursos essenciais por 14 dias.
              </p>
              <Button className="mt-8" size="lg" asChild>
                <Link to="/cadastro">
                  Criar minha agenda <ArrowRight size={18} />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}

type DemoTheme = 'light' | 'dark';
type DemoStatus = 'Confirmado' | 'Em atendimento' | 'Concluído';

const demoAppointments = [
  { time: '09:00', client: 'Cliente 01', service: 'Corte clássico' },
  { time: '10:15', client: 'Cliente 02', service: 'Barba completa' },
  { time: '11:00', client: 'Cliente 03', service: 'Corte + barba' },
] as const;

const demoStatusOrder: DemoStatus[] = ['Confirmado', 'Em atendimento', 'Concluído'];

function AgendaHeroDemo() {
  const [theme, setTheme] = useState<DemoTheme>('light');
  const [activeIndex, setActiveIndex] = useState(1);
  const [statuses, setStatuses] = useState<DemoStatus[]>([
    'Confirmado',
    'Em atendimento',
    'Confirmado',
  ]);
  const activeStatus = statuses[activeIndex] ?? 'Confirmado';

  const advanceStatus = () => {
    setStatuses((current) =>
      current.map((status, index) => {
        if (index !== activeIndex) return status;
        return (
          demoStatusOrder[(demoStatusOrder.indexOf(status) + 1) % demoStatusOrder.length] ?? status
        );
      }),
    );
  };

  return (
    <div className="relative mx-auto w-full max-w-[620px]">
      <div className="barber-stripe absolute -inset-3 rotate-[-1.7deg] rounded-[36px] opacity-80" />
      <section
        className="agenda-hero-demo relative overflow-hidden rounded-[32px] border p-4 shadow-[var(--shadow-lg)] sm:p-6"
        data-demo-theme={theme}
        aria-label="Demonstração interativa da agenda"
      >
        <header className="flex items-start justify-between gap-4 border-b border-[var(--demo-border)] pb-5">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-black uppercase tracking-[0.13em] text-[var(--demo-accent)]">
                Demonstração interativa
              </p>
              <span className="flex items-center gap-1 rounded-full bg-[var(--success-soft)] px-2 py-0.5 text-[10px] font-black text-[var(--success)]">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--success)] opacity-75" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-[var(--success)]" />
                </span>
                Ao vivo
              </span>
            </div>
            <h2 className="mt-1 text-xl font-extrabold text-[var(--demo-foreground)]">
              Agenda de hoje
            </h2>
            <p className="mt-1 text-xs text-[var(--demo-muted)]">
              Selecione um horário e avance o atendimento.
            </p>
          </div>
          <div
            className="flex shrink-0 rounded-xl border border-[var(--demo-border)] bg-[var(--demo-input)] p-1"
            role="group"
            aria-label="Tema da demonstração"
          >
            <button
              type="button"
              onClick={() => setTheme('light')}
              aria-pressed={theme === 'light'}
              className={cn(
                'grid size-9 place-items-center rounded-lg text-[var(--demo-muted)] transition-[background-color,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-accent)]',
                theme === 'light' &&
                  'motion-tab-selected bg-[var(--demo-surface)] text-[var(--demo-foreground)] shadow-[var(--shadow-sm)]',
              )}
              aria-label="Exibir demonstração em tema claro"
            >
              <Sun size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setTheme('dark')}
              aria-pressed={theme === 'dark'}
              className={cn(
                'grid size-9 place-items-center rounded-lg text-[var(--demo-muted)] transition-[background-color,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-accent)]',
                theme === 'dark' &&
                  'motion-tab-selected bg-[var(--demo-surface)] text-[var(--demo-foreground)] shadow-[var(--shadow-sm)]',
              )}
              aria-label="Exibir demonstração em tema escuro"
            >
              <Moon size={16} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="mt-5 grid gap-2">
          {demoAppointments.map((appointment, index) => {
            const status = statuses[index] ?? 'Confirmado';
            const isActive = index === activeIndex;
            return (
              <button
                key={appointment.time}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                className={cn(
                  'grid min-h-16 w-full grid-cols-[50px_1fr_auto] items-center gap-3 rounded-2xl border p-3 text-left transition-[background-color,border-color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-accent)]',
                  isActive
                    ? 'motion-choice-selected border-[var(--demo-brand)] bg-[var(--demo-selected)] shadow-[0_0_0_1px_var(--demo-brand)]'
                    : 'border-[var(--demo-border)] bg-[var(--demo-input)] hover:bg-[var(--demo-surface)]',
                )}
              >
                <span className="text-sm font-black tabular-nums text-[var(--demo-foreground)]">
                  {appointment.time}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-extrabold text-[var(--demo-foreground)]">
                    {appointment.client}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-[var(--demo-muted)]">
                    {appointment.service}
                  </span>
                </span>
                <span className="agenda-demo-status" data-status={status}>
                  {status}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-[var(--demo-border)] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-bold text-[var(--demo-muted)]" aria-live="polite">
            {demoAppointments[activeIndex]?.time} selecionado, {activeStatus.toLowerCase()}.
          </p>
          <button
            type="button"
            onClick={advanceStatus}
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[var(--demo-brand)] px-4 text-xs font-extrabold text-[var(--demo-brand-foreground)] transition-[background-color,transform] duration-200 hover:bg-[var(--demo-brand-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--demo-accent)] active:translate-y-px"
          >
            <CheckCircle2 size={16} aria-hidden="true" /> Avançar atendimento
          </button>
        </div>
      </section>
    </div>
  );
}

// --- Social Proof (Avatar Stack + Rating) ---
function SocialProof() {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-4">
      <div className="flex items-center">
        {/* Avatar stack: colored initials circles */}
        {[['L','oklch(0.47 0.065 165)'],['M','oklch(0.5 0.085 45)'],['R','oklch(0.43 0.075 155)'],['K','oklch(0.47 0.075 75)'],['B','oklch(0.47 0.08 25)']].map(([initial, color], i) => (
          <span
            key={initial}
            aria-hidden="true"
            className="relative flex size-8 items-center justify-center rounded-full border-2 border-[var(--background)] text-[10px] font-black text-white"
            style={{ background: color, marginLeft: i === 0 ? 0 : '-8px', zIndex: 5 - i }}
          >
            {initial}
          </span>
        ))}
      </div>
      <div>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map((n) => (
            <Star key={n} size={12} className="fill-[var(--accent)] text-[var(--accent)]" />
          ))}
          <span className="ml-1 text-xs font-extrabold">4.9</span>
        </div>
        <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">
          +450 barbearias organizando a rotina hoje
        </p>
      </div>
    </div>
  );
}

// --- Mobile Sticky CTA Bar ---
function MobileStickyCTA() {
  const heroRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => { setVisible(entry != null && !entry.isIntersecting); },
      { threshold: 0.1 },
    );
    observer.observe(hero);
    heroRef.current = hero;
    return () => observer.disconnect();
  }, []);

  return (
    <div
      aria-hidden={!visible}
      className={cn(
        'mobile-cta-bar safe-bottom fixed inset-x-0 bottom-0 z-50 px-4 pb-4 transition-[transform,opacity] duration-300 md:hidden',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none',
      )}
    >
      <Button size="lg" className="w-full shadow-[var(--shadow-lg)]" asChild>
        <Link to="/cadastro">
          Testar 14 Dias Grátis <ArrowRight size={18} />
        </Link>
      </Button>
    </div>
  );
}
